import store from '../index';
import {
  addCentralizedError,
  showErrorModal,
  EmployeeUpdateError,
} from '../slices/employeeUpdatesSlice';
import { generateErrorId, extractOriginalMessage } from './errorHandlerBase';

export const EMPLOYEE_UPDATE_API_NAMES = {
  FETCH: 'fetchEmployees',
  CREATE: 'createEmployee',
  LOOKUP_GPID: 'lookupEmployeeByGpid',
  DELETE: 'deleteEmployee',
  UPDATE: 'updateEmployee',
} as const;

export const getUserFriendlyErrorMessage = (apiName: string, originalMessage?: string): string => {
  const friendly: Record<string, string> = {
    [EMPLOYEE_UPDATE_API_NAMES.FETCH]: 'Unable to load employees. Please try again.',
    [EMPLOYEE_UPDATE_API_NAMES.CREATE]: 'Unable to create employee. Please try again.',
    [EMPLOYEE_UPDATE_API_NAMES.LOOKUP_GPID]: 'You can continue entering the details manually.',
    [EMPLOYEE_UPDATE_API_NAMES.DELETE]: 'Unable to delete employee. Please try again.',
    [EMPLOYEE_UPDATE_API_NAMES.UPDATE]: 'Unable to update employee. Please try again.',
  };

  return friendly[apiName] || `An error occurred. ${originalMessage || 'Please try again.'}`;
};

export const handleEmployeeUpdateErrorWithFriendlyMessage = (
  apiName: string,
  error: any,
  displayModal: boolean = true,
  context: any = {}
): EmployeeUpdateError => {
  const originalMessage = extractOriginalMessage(error);
  const friendlyMessage = getUserFriendlyErrorMessage(apiName, originalMessage);

  // normalize original message so it ends with punctuation when included in concatenated message
  const trimmedOriginal = originalMessage ? String(originalMessage).trim() : '';
  const originalForDisplay = trimmedOriginal
    ? /[.!?]$/.test(trimmedOriginal)
      ? trimmedOriginal
      : `${trimmedOriginal}.`
    : '';

  // If we have an API original message, reorder the friendly text so that any trailing
  // "Please try again." appears at the end: "<friendly base> <originalMessage> Please try again."
  // This produces: "Unable to create employee. Employee with this GPID already exists. Please try again."
  // Special case for LOOKUP_GPID: show original message first, then manual entry text
  let concatenatedMessage: string;
  const retrySuffix = ' Please try again.';
  if (trimmedOriginal) {
    if (apiName === EMPLOYEE_UPDATE_API_NAMES.LOOKUP_GPID) {
      // For GPID lookup: "Original message. You can continue entering the details manually."
      concatenatedMessage = `${originalForDisplay} ${friendlyMessage}`;
    } else if (friendlyMessage.endsWith(retrySuffix)) {
      const base = friendlyMessage.slice(0, -retrySuffix.length);
      concatenatedMessage = `${base} ${originalForDisplay}${retrySuffix}`;
    } else {
      concatenatedMessage = `${friendlyMessage} ${originalForDisplay}`;
    }
  } else {
    concatenatedMessage = friendlyMessage;
  }

  const centralizedError: EmployeeUpdateError = {
    id: generateErrorId(),
    message: concatenatedMessage,
    details: originalMessage,
    apiName,
    timestamp: Date.now(),
    // include originalMessage in context so UI overlays can show API-provided message
    context: { originalMessage, ...context },
  };

  try {
    store.dispatch(addCentralizedError(centralizedError));
    if (displayModal) {
      store.dispatch(showErrorModal(centralizedError));
    }
  } catch (e) {
    console.error('[EmployeeUpdate Error] failed to dispatch centralized error:', e);
  }

  console.error(`[EmployeeUpdate Error] ${apiName}:`, {
    message: friendlyMessage,
    errorId: centralizedError.id,
    originalError: error,
    context,
  });

  return centralizedError;
};

export const safeEmployeeUpdateApiCall = async <T>(
  apiCall: () => Promise<T>,
  apiName: string,
  displayModal: boolean = true,
  context?: any
): Promise<T | null> => {
  try {
    const result = await apiCall();
    return result;
  } catch (err) {
    handleEmployeeUpdateErrorWithFriendlyMessage(apiName, err, displayModal, context);
    return null;
  }
};
